import type { LiveToolActivity, Message, MessageContent } from './types';

const TOOL_LABELS: Record<string, string> = {
  search: 'Search',
  search_notes: 'Search notes',
  list_workspace: 'List workspace',
  fetch: 'Fetch',
  read_block: 'Read block',
  read_page: 'Read page',
  create_page: 'Create page',
  edit_page: 'Edit page',
  move_page: 'Move page',
  trash_page: 'Move to trash',
  update_database_schema: 'Update database',
  query_database: 'Query database',
  list_recent_changes: 'List recent changes',
  create_block: 'Create block',
  update_block: 'Update block',
  create_database_row: 'Create database row',
  update_database_row: 'Update database row',
  delete_database_row: 'Delete database row',
};

export type ToolActivityLine = {
  ok: boolean;
  title: string;
  detail: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value);
}

function quote(value: string): string {
  return `"${value}"`;
}

function shortId(value: string): string {
  return value.length > 8 ? `${value.slice(0, 8)}…` : value;
}

export function toolDisplayName(name: string): string {
  return TOOL_LABELS[name] ?? name.replaceAll('_', ' ');
}

function targetBlockLabel(args: Record<string, unknown>): string {
  if (typeof args.targetTitle === 'string' && args.targetTitle.trim()) {
    return quote(args.targetTitle.trim());
  }
  return '';
}

export function toolCallArgsSummary(name: string, args: Record<string, unknown>): string {
  switch (name) {
    case 'search':
    case 'search_notes':
      return typeof args.query === 'string' ? quote(args.query) : '';
    case 'fetch':
    case 'edit_page':
    case 'trash_page':
    case 'read_block':
    case 'read_page': {
      const label = targetBlockLabel(args);
      return label || (typeof args.id === 'string' ? shortId(args.id) : '');
    }
    case 'query_database': {
      const parts: string[] = [];
      const label = targetBlockLabel(args);
      if (label) {
        parts.push(label);
      } else if (typeof args.databaseId === 'string') {
        parts.push(shortId(args.databaseId));
      }
      if (Array.isArray(args.filter) && args.filter.length > 0) {
        parts.push(`${args.filter.length} filter${args.filter.length === 1 ? '' : 's'}`);
      }
      if (Array.isArray(args.sort) && args.sort.length > 0) {
        parts.push(`sort ×${args.sort.length}`);
      }
      if (typeof args.limit === 'number') {
        parts.push(`limit ${args.limit}`);
      }
      return parts.join(', ');
    }
    case 'create_page':
    case 'create_block': {
      const parts: string[] = [];
      if (typeof args.type === 'string') {
        parts.push(args.type);
      }
      if (typeof args.title === 'string' && args.title.trim()) {
        parts.push(quote(args.title.trim()));
      }
      return parts.join(' ');
    }
    case 'update_block': {
      const parts: string[] = [];
      const label = targetBlockLabel(args);
      if (label) {
        parts.push(label);
      } else if (typeof args.id === 'string') {
        parts.push(shortId(args.id));
      }
      if (typeof args.title === 'string' && args.title.trim()) {
        parts.push(`→ ${quote(args.title.trim())}`);
      }
      return parts.join(' ');
    }
    case 'create_database_row': {
      const parts: string[] = [];
      const label = targetBlockLabel(args);
      if (label) {
        parts.push(label);
      } else if (typeof args.databaseId === 'string') {
        parts.push(shortId(args.databaseId));
      }
      if (typeof args.title === 'string' && args.title.trim()) {
        parts.push(quote(args.title.trim()));
      }
      return parts.join(' ');
    }
    case 'update_database_row':
    case 'delete_database_row': {
      const label = targetBlockLabel(args);
      return label || (typeof args.rowId === 'string' ? shortId(args.rowId) : '');
    }
    case 'list_recent_changes': {
      const parts: string[] = [];
      if (typeof args.since === 'string') {
        parts.push(`since ${args.since.slice(0, 10)}`);
      }
      if (typeof args.limit === 'number') {
        parts.push(`limit ${args.limit}`);
      }
      return parts.join(', ');
    }
    default:
      return '';
  }
}

function parseToolResult(raw: unknown): Record<string, unknown> | null {
  if (isRecord(raw)) {
    return raw;
  }
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function blockTitle(value: unknown): string | null {
  if (!isRecord(value)) {
    return null;
  }
  if (typeof value.title === 'string' && value.title.trim()) {
    return value.title.trim();
  }
  return null;
}

function namesFromResult(name: string, parsed: Record<string, unknown> | null): string {
  if (!parsed) {
    return '';
  }

  if ('error' in parsed) {
    const message =
      typeof parsed.message === 'string'
        ? parsed.message
        : typeof parsed.error === 'string'
          ? parsed.error
          : '';
    return message ? `Error: ${message}` : 'Error';
  }

  switch (name) {
    case 'search':
    case 'search_notes': {
      const results = parsed.results;
      if (!Array.isArray(results)) {
        return '';
      }
      if (results.length === 0) {
        return 'No matches';
      }
      const seen = new Set<string>();
      const titles: string[] = [];
      let firstSnippet = '';
      for (const row of results) {
        if (!isRecord(row)) {
          continue;
        }
        const containerId =
          typeof row.containerId === 'string'
            ? row.containerId
            : typeof row.id === 'string'
              ? row.id
              : '';
        const label =
          typeof row.containerTitle === 'string' && row.containerTitle.trim()
            ? row.containerTitle.trim()
            : (blockTitle(row) ?? 'Untitled');
        if (!firstSnippet && typeof row.snippet === 'string' && row.snippet.trim()) {
          firstSnippet = row.snippet.trim();
        }
        const key = containerId || label;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        titles.push(label);
        if (titles.length >= 4) {
          break;
        }
      }
      const suffix =
        seen.size < results.length ? ` +${results.length - seen.size} more` : '';
      const preview = `${titles.join(', ')}${suffix}`;
      if (firstSnippet) {
        return `${preview} — ${truncateSummary(firstSnippet, 80)}`;
      }
      return preview;
    }
    case 'read_block': {
      const title = blockTitle(parsed.block);
      return title ? title : '';
    }
    case 'fetch':
    case 'create_page':
    case 'edit_page': {
      const title =
        typeof parsed.title === 'string' && parsed.title.trim()
          ? parsed.title.trim()
          : blockTitle(parsed.page);
      return title ?? '';
    }
    case 'read_page': {
      const title = blockTitle(parsed.page);
      const markdown = typeof parsed.markdown === 'string' ? parsed.markdown.length : 0;
      if (title && markdown > 0) {
        return `${title} (${Math.ceil(markdown / 1000)}k chars)`;
      }
      return title ?? '';
    }
    case 'query_database': {
      const rows = parsed.rows;
      const columns = parsed.columns;
      const rowCount = Array.isArray(rows) ? rows.length : null;
      const columnCount = Array.isArray(columns) ? columns.length : null;
      const title = typeof parsed.title === 'string' && parsed.title.trim() ? parsed.title.trim() : '';
      const parts: string[] = [];
      if (title) {
        parts.push(`"${title}"`);
      }
      if (rowCount != null) {
        parts.push(`${rowCount} row${rowCount === 1 ? '' : 's'}`);
      }
      if (columnCount != null) {
        parts.push(`${columnCount} column${columnCount === 1 ? '' : 's'}`);
      }
      return parts.join(', ');
    }
    case 'list_recent_changes': {
      const changes = parsed.changes;
      if (!Array.isArray(changes)) {
        return '';
      }
      if (changes.length === 0) {
        return 'No recent changes';
      }
      const preview = changes
        .slice(0, 3)
        .map((row) => {
          if (!isRecord(row)) {
            return 'change';
          }
          const op = typeof row.operation === 'string' ? row.operation : 'change';
          const label =
            typeof row.title === 'string' && row.title.trim()
              ? row.title.trim()
              : typeof row.tableName === 'string'
                ? row.tableName
                : 'row';
          return `${op} ${label}`;
        })
        .join(', ');
      const suffix = changes.length > 3 ? ` +${changes.length - 3} more` : '';
      return `${preview}${suffix}`;
    }
    case 'create_block':
    case 'update_block': {
      const title = blockTitle(parsed.block);
      const type = isRecord(parsed.block) && typeof parsed.block.type === 'string' ? parsed.block.type : '';
      if (title && type) {
        return `${type} "${title}"`;
      }
      return title ?? type;
    }
    case 'create_database_row':
    case 'update_database_row': {
      const title = blockTitle(parsed.row);
      return title ? `"${title}"` : '';
    }
    case 'delete_database_row':
      return parsed.deleted === true ? 'Row deleted' : '';
    default:
      return '';
  }
}

function toolMessageArgs(message: Message): Record<string, unknown> | undefined {
  const args = message.content.args;
  return isRecord(args) ? args : undefined;
}

function toolResultFailed(content: MessageContent): boolean {
  if (content.ok === false) {
    return true;
  }
  const parsed = parseToolResult(content.result);
  return parsed != null && 'error' in parsed;
}

function buildToolDetail(name: string, content: MessageContent): string {
  const parsed = parseToolResult(content.result);
  const fromResult = namesFromResult(name, parsed);

  if (typeof content.summary === 'string' && content.summary.trim()) {
    const summary = content.summary.trim();
    if (!fromResult || summary.includes(fromResult)) {
      return summary;
    }
    return `${summary} — ${fromResult}`;
  }

  if (fromResult) {
    return fromResult;
  }

  if (parsed && 'message' in parsed && typeof parsed.message === 'string') {
    return parsed.message;
  }

  return '';
}

export function formatHistoryToolRow(message: Message): ToolActivityLine {
  const name = message.toolName ?? 'tool';
  const args = toolMessageArgs(message);
  const argsWithTitle =
    args && typeof message.content.targetTitle === 'string' && !args.targetTitle
      ? { ...args, targetTitle: message.content.targetTitle }
      : args;
  const argsPart = argsWithTitle ? toolCallArgsSummary(name, argsWithTitle) : '';
  const title = argsPart ? `${toolDisplayName(name)} · ${argsPart}` : toolDisplayName(name);
  const detail = buildToolDetail(name, message.content);

  return {
    ok: !toolResultFailed(message.content),
    title,
    detail,
  };
}

export function formatLiveToolRow(activity: LiveToolActivity): ToolActivityLine {
  const argsPart = activity.argsSummary?.trim() ?? '';
  const title = argsPart
    ? `${toolDisplayName(activity.name)} · ${argsPart}`
    : toolDisplayName(activity.name);
  const detail = activity.summary?.trim() ?? '';

  return {
    ok: activity.status !== 'error',
    title,
    detail,
  };
}

export function formatToolGroupLabel(
  items: Array<{ kind: 'live'; activity: LiveToolActivity } | { kind: 'history'; message: Message }>,
): string {
  const previews = items.slice(0, 3).map((item) => {
    if (item.kind === 'live') {
      return formatLiveToolRow(item.activity).title;
    }
    return formatHistoryToolRow(item.message).title;
  });
  const suffix = items.length > 3 ? ` +${items.length - 3} more` : '';
  return `${previews.join('; ')}${suffix}`;
}

/** @deprecated Use formatHistoryToolRow().detail */
export function toolMessageSummary(message: Message): string {
  return formatHistoryToolRow(message).detail;
}

export function truncateSummary(text: string, max = 240): string {
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max - 1)}…`;
}

export function liveActivityFromToolCall(
  name: string,
  args: Record<string, unknown>,
  id: string,
): LiveToolActivity {
  return {
    id,
    name,
    argsSummary: toolCallArgsSummary(name, args),
    status: 'running',
  };
}
